import { useCallback, useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, Minus, Plus, Clock, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { requestMoreSeats, getMySeatRequest } from "@/services/adminService";

// Backend errors come back as { error } (fail helper) or { message }.
const errMsg = (err, fallback) =>
  err?.response?.data?.error ||
  err?.response?.data?.message ||
  err?.message ||
  fallback;

/**
 * "Request more seats" dialog for OFF-PLATFORM companies (no Razorpay).
 *
 * These companies can't self-serve buy seats, so instead of a dead-end the
 * admin asks the super-admin to raise the licensed seat count. Flow:
 * pick how many MORE seats → optional note → POST /admin/seat-requests.
 * The super-admin approves it, which bumps number_of_employees.
 *
 * Props:
 *   open, onOpenChange   – controlled visibility
 *   initialSeats         – seats to prefill (e.g. the limit shortfall). Default 1.
 *   onRequested          – called after a request is successfully submitted
 */
export default function RequestSeatsDialog({
  open,
  onOpenChange,
  initialSeats = 1,
  planLimit,
  currentSeats,
  planName,
  onRequested,
}) {
  const [seats, setSeats] = useState(Math.max(1, initialSeats || 1));
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [pending, setPending] = useState(null);
  const [loadingPending, setLoadingPending] = useState(false);

  // Plan's hard employee cap. -1 / null = no cap (unlimited/unknown). "seats"
  // here is how many MORE seats to request, so the max we can add is
  // (cap − currentSeats). At/over the cap they must upgrade the plan instead.
  const hasCap = planLimit != null && Number(planLimit) !== -1;
  const cap = hasCap ? Number(planLimit) : Infinity;
  const licensed = Number(currentSeats || 0);
  const maxExtra = hasCap ? Math.max(0, cap - licensed) : Infinity;
  const atCap = hasCap && maxExtra <= 0;
  const overCap = hasCap && seats > maxExtra;

  // On open: reset inputs and check for an already-pending request.
  useEffect(() => {
    if (!open) return;
    setSeats(Math.max(1, initialSeats || 1));
    setNote("");
    setPending(null);
    setLoadingPending(true);
    (async () => {
      try {
        const res = await getMySeatRequest();
        setPending(res?.pending || null);
      } catch (_) {
        setPending(null);
      } finally {
        setLoadingPending(false);
      }
    })();
  }, [open, initialSeats]);

  const changeSeats = (delta) =>
    setSeats((s) => {
      const hardMax = hasCap ? Math.min(1000, maxExtra) : 1000;
      return Math.max(1, Math.min(hardMax, (Number(s) || 1) + delta));
    });

  const handleSubmit = useCallback(async () => {
    if (!seats || seats < 1) return;
    if (overCap || atCap) return;
    setSubmitting(true);
    try {
      await requestMoreSeats(seats, note.trim() || undefined);
      toast.success(
        `Request for ${seats} more seat${seats > 1 ? "s" : ""} sent to your administrator.`
      );
      onOpenChange(false);
      onRequested?.();
    } catch (err) {
      // 409 = a pending request already exists; surface it inline.
      if (err?.response?.status === 409) {
        setPending(err?.response?.data?.request || { status: "pending" });
        toast.info("You already have a pending seat request.");
      } else {
        toast.error(errMsg(err, "Couldn't send the request. Please try again."));
      }
    } finally {
      setSubmitting(false);
    }
  }, [seats, note, overCap, atCap, onOpenChange, onRequested]);

  const hasPending = !!pending;

  return (
    <Dialog open={open} onOpenChange={(v) => !submitting && onOpenChange(v)}>
      <DialogContent className="max-w-md">
        <DialogHeader className="space-y-1">
          <DialogTitle className="text-xl font-semibold">
            Request more seats
          </DialogTitle>
          <DialogDescription>
            Your plan's seats are managed by your administrator. Ask them to add
            more employees to your plan and they'll be enabled once approved.
          </DialogDescription>
        </DialogHeader>

        {loadingPending ? (
          <div className="flex items-center gap-2 py-6 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Checking existing
            requests…
          </div>
        ) : hasPending ? (
          <div className="space-y-3 py-2">
            <div className="flex items-start gap-3 rounded-md border bg-muted/40 p-3 text-sm">
              <Clock className="mt-0.5 h-4 w-4 shrink-0 text-amber-500" />
              <div>
                <p className="font-medium">Request pending approval</p>
                <p className="text-muted-foreground">
                  You've asked for{" "}
                  <span className="font-medium text-foreground">
                    {pending.requested_seats}
                  </span>{" "}
                  total seats. Your administrator will review it shortly. You
                  can send a new request once this one is resolved.
                </p>
              </div>
            </div>
          </div>
        ) : atCap ? (
          <div className="space-y-3 py-2">
            <div className="flex items-start gap-3 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm dark:border-amber-900/40 dark:bg-amber-900/10">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-500" />
              <div>
                <p className="font-medium">You've reached your plan's limit</p>
                <p className="text-muted-foreground">
                  The {planName || "current"} plan allows up to{" "}
                  <span className="font-medium text-foreground">{cap}</span> employees,
                  and all {licensed} are licensed. Adding more needs a{" "}
                  <span className="font-medium text-foreground">plan upgrade</span> —
                  contact your administrator to move to a larger plan.
                </p>
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-4 py-1">
            <div className="space-y-2">
              <Label htmlFor="seat-count">How many more seats do you need?</Label>
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  onClick={() => changeSeats(-1)}
                  disabled={submitting || seats <= 1}
                  aria-label="Decrease"
                >
                  <Minus className="h-4 w-4" />
                </Button>
                <Input
                  id="seat-count"
                  type="number"
                  min={1}
                  max={hasCap ? maxExtra : 1000}
                  value={seats}
                  onWheel={(e) => e.target.blur()}
                  onChange={(e) => {
                    const hardMax = hasCap ? Math.min(1000, maxExtra) : 1000;
                    setSeats(
                      Math.max(1, Math.min(hardMax, parseInt(e.target.value, 10) || 1))
                    );
                  }}
                  className="w-24 text-center"
                  disabled={submitting}
                />
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  onClick={() => changeSeats(1)}
                  disabled={submitting || seats >= (hasCap ? maxExtra : 1000)}
                  aria-label="Increase"
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
              {hasCap ? (
                overCap ? (
                  <p className="flex items-center gap-1 text-xs font-medium text-rose-600 dark:text-rose-400">
                    <AlertTriangle className="h-3.5 w-3.5" />
                    Your {planName || "plan"} allows up to {cap} employees — you can
                    add at most {maxExtra} more without upgrading.
                  </p>
                ) : (
                  <p className="text-xs text-muted-foreground">
                    You have {licensed} of {cap} plan seats licensed —{" "}
                    <span className="font-medium text-foreground">{maxExtra}</span> more
                    available on this plan.
                  </p>
                )
              ) : null}
            </div>

            <div className="space-y-2">
              <Label htmlFor="seat-note">Note to your administrator (optional)</Label>
              <Textarea
                id="seat-note"
                placeholder="e.g. Onboarding 3 new hires this week."
                value={note}
                maxLength={500}
                onChange={(e) => setNote(e.target.value)}
                disabled={submitting}
                rows={3}
              />
            </div>
          </div>
        )}

        <DialogFooter className="flex justify-end gap-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={submitting}
          >
            {hasPending || atCap ? "Close" : "Cancel"}
          </Button>
          {!hasPending && !atCap && (
            <Button
              onClick={handleSubmit}
              disabled={submitting || loadingPending || overCap}
            >
              {submitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Sending…
                </>
              ) : (
                "Send request"
              )}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
